import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  Appointment,
  AppointmentWeek,
  CreateAppointmentInput,
  SetAppointmentStatusInput,
  UpdateAppointmentInput,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import {
  createAppointment,
  getAppointmentWeek,
  setAppointmentStatus,
  updateAppointment,
  type AppointmentWeekParams,
} from './appointments-api.js';

/**
 * The appointments hooks.
 *
 * NONE OF THESE GOES THROUGH THE OFFLINE WRITE QUEUE, for the same reason a check-in does not: a
 * booking is a promise made to a person standing at the counter. Replaying "book Priya at four"
 * from a queue twenty minutes after the connection came back — against a slot that may by then
 * be taken by someone else — is not a promise this screen is allowed to make silently. A booking
 * that fails to send is one the counter makes again, deliberately, and finds out right away
 * whether the slot is still open.
 */

export const appointmentKeys = {
  all: ['appointments'] as const,
  week: (params: AppointmentWeekParams) => ['appointments', 'week', params] as const,
};

/**
 * One week's grid. `placeholderData` keeps the week already on screen while the next one loads,
 * so stepping forward or back a week does not blank the grid under somebody's hand.
 */
export function useAppointmentWeek(params: AppointmentWeekParams): UseQueryResult<AppointmentWeek> {
  const client = useApiClient();
  return useQuery({
    queryKey: appointmentKeys.week(params),
    queryFn: () => getAppointmentWeek(client, params),
    placeholderData: (previous) => previous,
  });
}

/** Invalidate every week on screen — a write can move an appointment across a week boundary. */
function useAppointmentInvalidation(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
  };
}

export function useCreateAppointment(): UseMutationResult<
  Appointment,
  unknown,
  CreateAppointmentInput
> {
  const client = useApiClient();
  const invalidate = useAppointmentInvalidation();
  return useMutation({
    mutationFn: (input: CreateAppointmentInput) => createAppointment(client, input),
    onSuccess: invalidate,
  });
}

export function useUpdateAppointment(): UseMutationResult<
  Appointment,
  unknown,
  { appointmentId: string; patch: UpdateAppointmentInput }
> {
  const client = useApiClient();
  const invalidate = useAppointmentInvalidation();
  return useMutation({
    mutationFn: ({
      appointmentId,
      patch,
    }: {
      appointmentId: string;
      patch: UpdateAppointmentInput;
    }) => updateAppointment(client, appointmentId, patch),
    onSuccess: invalidate,
  });
}

export function useSetAppointmentStatus(): UseMutationResult<
  Appointment,
  unknown,
  { appointmentId: string; input: SetAppointmentStatusInput }
> {
  const client = useApiClient();
  const invalidate = useAppointmentInvalidation();
  return useMutation({
    mutationFn: ({
      appointmentId,
      input,
    }: {
      appointmentId: string;
      input: SetAppointmentStatusInput;
    }) => setAppointmentStatus(client, appointmentId, input),
    onSuccess: invalidate,
  });
}
